// Keep updated with SubGroupUserMembership in cryptoview/models/group.py
import { SortingOrder } from "types";
import { SortOrder } from "antd/es/table/interface";
import {
  CHANGE_FULL_NAME_FEATURE_FLAG,
  HEDGING_BOTS_FEATURE_FLAG,
  IS_MOBILE_WEBVIEW_WINDOW_KEY,
  MCP_FEATURE_FLAG,
} from "@shared/common/constants";
import { LanguageNameSpaces } from "@shared/types";
import { TranslationKeys } from "@shared/common/i18n/types.ts";
import {
  CHANGE_NAME_SETTINGS_PATH,
  HEDGING_BOT_PATH,
  MCP_PATH,
  WHOP_EXPERIENCE_DEFAULT_PATH,
} from "common/pathConstants.ts";

export * from "common/pathConstants.ts";

export const USER_MEMBERSHIP_ACTIVE_STATUS = "ACTIVE";
export const USER_MEMBERSHIP_USER_LEFT_STATUS = "USER_LEFT";
export const USER_MEMBERSHIP_ADMIN_KICKED_STATUS = "ADMIN_KICKED";
export const USER_MEMBERSHIP_BOT_KICKED_STATUS = "BOT_KICKED";
export const USER_MEMBERSHIP_UNAUTHORIZED_STATUS = "UNAUTHORIZED";
export const USER_MEMBERSHIP_INACTIVE_UNCLASSIFIED_STATUS =
  "INACTIVE_UNCLASSIFIED";
export const USER_MEMBERSHIP_UNAUTHORIZED_USER_LEFT_STATUS =
  "UNAUTHORIZED_USER_LEFT";
export const USER_MEMBERSHIP_AUTHORIZED_PENDING_STATUS = "AUTHORIZED_PENDING";
export const USER_MEMBERSHIP_EXPIRED_KICKED_STATUS = "EXPIRED_KICKED";
export const USER_MEMBERSHIP_EXPIRED_IN_GROUP_STATUS = "EXPIRED_IN_GROUP";

export const NOT_IN_GROUP_STATUSES = [
  USER_MEMBERSHIP_USER_LEFT_STATUS,
  USER_MEMBERSHIP_ADMIN_KICKED_STATUS,
  USER_MEMBERSHIP_BOT_KICKED_STATUS,
  USER_MEMBERSHIP_INACTIVE_UNCLASSIFIED_STATUS,
  USER_MEMBERSHIP_UNAUTHORIZED_USER_LEFT_STATUS,
  USER_MEMBERSHIP_AUTHORIZED_PENDING_STATUS,
  USER_MEMBERSHIP_EXPIRED_KICKED_STATUS,
];

export const INACTIVE_GROUP_STATUSES = [
  USER_MEMBERSHIP_INACTIVE_UNCLASSIFIED_STATUS,
  USER_MEMBERSHIP_USER_LEFT_STATUS,
  USER_MEMBERSHIP_UNAUTHORIZED_USER_LEFT_STATUS,
];

export const PENDING_GROUP_STATUSES = [
  USER_MEMBERSHIP_AUTHORIZED_PENDING_STATUS,
];

export const KICKED_GROUP_STATUSES = [
  USER_MEMBERSHIP_ADMIN_KICKED_STATUS,
  USER_MEMBERSHIP_BOT_KICKED_STATUS,
  USER_MEMBERSHIP_EXPIRED_KICKED_STATUS,
];

export const SUB_GROUP_INVITATION_ACTIVE_STATUS = "ACTIVE";
export const SUB_GROUP_INVITATION_INACTIVE_STATUS = "INACTIVE";

export const SUB_GROUP_CONFIGURATION_KICK_EXPIRED_USERS = "kick_expired_users";
export const SUB_GROUP_CONFIGURATION_KICK_UNAUTHORIZED_USERS =
  "kick_unauthorized_users";

// Create order constants
export const ENTRY_ORDER = "entry";
export const TAKE_PROFIT_ORDER = "take_profit";
export const MARKET_ORDER_TYPE = "market";
export const STOP_LIMIT_ORDER_TYPE = "stop_limit";
export const STOP_MARKET_ORDER_TYPE = "stop_market";
export const LIMIT_ORDER_TYPE = "limit";

export const ORDER_TYPE_TO_DISPLAY: Record<
  string,
  TranslationKeys<LanguageNameSpaces.PORTFOLIO>
> = {
  [MARKET_ORDER_TYPE]: "create_order.order_type.market",
  [LIMIT_ORDER_TYPE]: "create_order.order_type.limit",
  [STOP_LIMIT_ORDER_TYPE]: "create_order.order_type.stop_limit",
  [STOP_MARKET_ORDER_TYPE]: "create_order.order_type.stop_market",
};

export const COIN_EXTRA_DETAILS_TABS = Object.freeze({
  GENERAL: "General",
  TRADES: "Trades",
  ACCOUNTS: "Accounts",
});
export const ACCOUNT_EXTRA_DETAILS_TABS = Object.freeze({
  GENERAL: "General",
  TRADES: "Trades",
  ASSETS: "Assets",
  POSITIONS: "Positions",
});
export const GROUP_MANAGEMENT_TABS = Object.freeze({
  USERS: "Users",
  INVITATIONS: "Invitations",
  CONFIGURATION: "Configuration",
});

export const COIN_EXTRA_DETAILS_TABS_TO_DISPLAY: Record<
  string,
  TranslationKeys<LanguageNameSpaces.PORTFOLIO>
> = {
  [COIN_EXTRA_DETAILS_TABS.GENERAL]: "drawer_tabs.general",
  [COIN_EXTRA_DETAILS_TABS.TRADES]: "drawer_tabs.trades",
  [COIN_EXTRA_DETAILS_TABS.ACCOUNTS]: "drawer_tabs.accounts",
};
export const ACCOUNT_EXTRA_DETAILS_TABS_TO_DISPLAY: Record<
  string,
  TranslationKeys<LanguageNameSpaces.PORTFOLIO>
> = {
  [ACCOUNT_EXTRA_DETAILS_TABS.GENERAL]: "drawer_tabs.general",
  [ACCOUNT_EXTRA_DETAILS_TABS.TRADES]: "drawer_tabs.trades",
  [ACCOUNT_EXTRA_DETAILS_TABS.ASSETS]: "drawer_tabs.assets",
  [ACCOUNT_EXTRA_DETAILS_TABS.POSITIONS]: "drawer_tabs.positions",
};
export const GROUP_MANAGEMENT_TABS_TO_DISPLAY: Record<
  string,
  TranslationKeys<LanguageNameSpaces.ADMIN>
> = {
  [GROUP_MANAGEMENT_TABS.USERS]: "users.tabs.users",
  [GROUP_MANAGEMENT_TABS.INVITATIONS]: "users.tabs.invitations",
  [GROUP_MANAGEMENT_TABS.CONFIGURATION]: "users.tabs.configuration",
};

export const TIME_RANGE_START = "start";
export const TIME_RANGE_1_HOUR = "1h";
export const TIME_RANGE_6_HOURS = "6h";
export const TIME_RANGE_12_HOURS = "12h";
export const TIME_RANGE_1_DAY = "1d";
export const TIME_RANGE_3_DAYS = "3d";
export const TIME_RANGE_1_WEEK = "1w";
export const TIME_RANGE_1_MONTHS = "1m";
export const TIME_RANGE_3_MONTHS = "3m";
export const TIME_RANGE_1_YEAR = "1y";
export const TIME_RANGES = [
  TIME_RANGE_START,
  TIME_RANGE_1_HOUR,
  TIME_RANGE_6_HOURS,
  TIME_RANGE_12_HOURS,
  TIME_RANGE_1_DAY,
  TIME_RANGE_3_DAYS,
  TIME_RANGE_1_WEEK,
  TIME_RANGE_1_MONTHS,
  TIME_RANGE_3_MONTHS,
  TIME_RANGE_1_YEAR,
];

export const DISPLAY_CURRENCIES = {
  BTC: "BTC",
  USD: "USD",
} as const;

export const AFFILIATION_FEATURE_FLAG = "affiliation";
export const CHANGE_LEVERAGE_FEATURE_FLAG = "change_leverage";
export const MARKETPLACE_PAYMENTS_FEATURE_FLAG = "marketplace_payments";
export const BULK_CREATE_BOTS_FEATURE_FLAG = "bulk_create_bots";
export const CREATE_ACCOUNT_INVITE_FEATURE_FLAG = "create_account_invite";
export const APPLE_PROMO_CODE_FEATURE_FLAG = "apple_promo_code";
export const NEW_PERMISSION_ACCOUNT_INVITE_FEATURE_FLAG =
  "new_permission_account_invite";

// Feature flags
export const PATH_TO_FEATURE_FLAG: Record<string, string> = {
  [CHANGE_NAME_SETTINGS_PATH]: CHANGE_FULL_NAME_FEATURE_FLAG,
  [HEDGING_BOT_PATH]: HEDGING_BOTS_FEATURE_FLAG,
  [MCP_PATH]: MCP_FEATURE_FLAG,
};

// Breakpoints
export const MOBILE_FILTERS = "1200px";
export const COLLAPSED_DESKTOP_MENU = "1400px";
export const MOBILE_SEARCH = "600px";
export const VERY_SMALL_MOBILE = "365px";
export const SMALL_MOBILE = "450px";
export const LARGE_MOBILE = "700px";
export const MOBILE_CREATE_TRADE = "900px";
export const MOBILE_MENU = "900px";
export const SMALL_SCREEN = "1100px";
export const HIDE_INTERCOM_LAUNCHER_BREAKPOINT = MOBILE_SEARCH;
export const TABLET = "800px";
export const LARGE_TABLET = "850px";
export const MOBILE_VIRTUAL_LIST = "750px";
export const MAX_DRAWER_EXTENSION_SCREEN = "1100px";
export const BOTTOM_NAVIGATOR = "450px";
export const NEW_LOOK_BOTTOM_NAVIGATOR = "600px";
export const MAX_BACKTEST_DRAWER_EXTENSION_SCREEN = "1450px";

export const PAGE_SPINNER_MARGIN_FROM_TOP = 100;

export const TELEGRAM_ANNOUNCEMENTS_LINK = "https://t.me/cornix_announcements";
export const FACEBOOK_LINK = "https://www.facebook.com/cornix.io";
export const INSTAGRAM_LINK =
  "https://www.instagram.com/cornix_io/?utm_medium=copy_link";
export const WEBSITE_LINK = "https://cornix.io/";
export const LINKEDIN_LINK = "https://www.linkedin.com/company/cornix-io";
export const YOUTUBE_LINK =
  "https://www.youtube.com/channel/UCzHyRmZfXrwXjL8an9QhSUw";

export const APPSTORE_DOWNLOAD_LINK =
  "https://apps.apple.com/il/app/cornix/id1490691026";
export const GOOGLE_PLAY_DOWNLOAD_LINK =
  "https://play.google.com/store/apps/details?id=com.cornix";
export const GENERIC_DOWNLOAD_LINK =
  "https://app.cornix.io/cornix_registration";

export const SORT_ORDER_MAPPING: Record<SortingOrder, SortOrder> = {
  [SortingOrder.ASCEND]: "ascend",
  [SortingOrder.DESCEND]: "descend",
} as const;

export const MAIN_CONTENT_ID = "mainContent";
export const CONTENT_WRAPPER_ID = "contentWrapper";

export const NEXT_URL_QUERY_PARAM = "next-path";

export const INTERCOM_ICON_SIZE = 48;
export const INTERCOM_BOTTOM = 20;
export const INTERCOM_TOTAL_SIZE = INTERCOM_ICON_SIZE + INTERCOM_BOTTOM;
export const BOTTOM_NAVIGATOR_HEIGHT = 56;

export const SIDER_COLLAPSED_SIZE = 50;
export const EXTRA_DETAILS_DRAWER_SCROLL_ID = "innerContentWrapper";

export const RESTRICTED_COUNTRY_TO_CODE = {
  Iran: "IR",
  Lebanon: "LB",
  Syria: "SY",
  "North Korea": "KP",
  Palestine: "PS",
  Iraq: "IQ",
  Somalia: "SO",
  "South Sudan": "SS",
  Cuba: "CU",
};
export const IS_MOBILE_WEBVIEW = !!(
  window as unknown as Record<string, unknown>
)[IS_MOBILE_WEBVIEW_WINDOW_KEY];
export const IS_WEB_VIEW = !!window.isReactNativeWebView;
function safeGetLocalStorageItem(key: string): string | null {
  try {
    return localStorage?.getItem(key);
  } catch {
    return null;
  }
}

export const STORAGE_IS_WHOP_KEY = "is_whop";
export const STORAGE_WHOP_EXPERIENCE_ID_KEY = "whop_experience_id";
export const IS_WHOP =
  window.location.pathname.startsWith(WHOP_EXPERIENCE_DEFAULT_PATH) ||
  !!safeGetLocalStorageItem(STORAGE_IS_WHOP_KEY);
export const WHOP_DEV_AUTH_TOKEN = new URLSearchParams(
  window.location.search
).get("whop-dev-user-token");

export const TABS_HEADER_HEIGHT = 60;
export const INIT_DATA_QUERY_PARAM = "initData";
export const QUICK_CONNECT_DATA_QUERY_PARAM = "quickConnectData";
export const LOCAL_STORAGE_TELEGRAM_QUICK_CONNECT_KEY =
  "telegram_quick_connect";
export const LOCAL_STORAGE_TELEGRAM_INIT_DATA_KEY = "telegram_init_data";
export const LOCAL_STORAGE_TG_LAST_WRITE_ACCESS_KEY =
  "telegram_last_write_request";
export const LOCAL_STORAGE_REMEMBER_ME_KEY = "remember_me";
export const SESSION_STORAGE_DISCORD_LINK_RETURN_KEY =
  "discord_link_return_path";
export const DISCORD_LINK_CODE_QUERY_PARAM = "discord_link_code";
export const TG_START_QUERY_PARAM = "tgWebAppStartParam";

export const DEFAULT_PADDING = 16;
export const SMALL_MOBILE_PADDING = 12;
export const DEFAULT_CHART_TOOLTIP_MARGIN = 8;
export const CHART_CUSTOM_POSITION_KEY = "custom_chart_cursor_position";

export const LOCAL_STORAGE_FAILED_PAYMENT_KEY = "is_failed_payment";

export const DRAWER_TRADES_TABLE_FULL_WIDTH = 900;
export const NOT_GROUP_MEMBER_MESSAGE =
  "You are not a member of the group you are trying to access.";

export const SCROLL_VIEW_REFETCH_Y_POSITION = 75;
export const SCROLL_VIEW_REFETCH_NEGATIVE_Y_POSITION = -35;
export const REFRESH_SPINNER_SIZE = 50;
export const REFRESH_SPINNER_INITIAL_Y = -(REFRESH_SPINNER_SIZE + 15);
export const REFRESH_SPINNER_LOADING_Y = 15;
export const PULL_TO_REFRESH_SLOP = 8;
export const PULL_TO_REFRESH_MIN_DURATION = 600;
export const PULL_TO_REFRESH_NO_REPORT_TIMEOUT = 1000;
export const NEW_LOOK_REFRESH_SPINNER_SIZE = 36;
export const NEW_LOOK_REFRESH_SPINNER_OFFSET =
  NEW_LOOK_REFRESH_SPINNER_SIZE + 8;
export const NEW_LOOK_REFRESH_THRESHOLD = 64;
export const NEW_LOOK_REFRESH_LOADING_DISTANCE = 56;
export const NEW_LOOK_PULL_MAX_DISTANCE = 128;
export const NEW_LOOK_PULL_RESISTANCE = 160;

export const ACTION_BUTTON_SIZE = 50;
export const ACTION_BUTTON_MARGIN = 16;
export const ACTION_BUTTON_WITH_MARGIN_HEIGHT =
  ACTION_BUTTON_SIZE + ACTION_BUTTON_MARGIN * 2;

export const CODE_SPLITTING_REFRESH_QUERY_PARAM = "is-refresh";

export const GENERAL_BACKEND_ERROR = "Internal error, please contact support";

export const DRAWER_VISIBILITY_CLASS = "drawer-hide-backface-visibility";
export const BOTTOM_SUMMARY_CLASS = "bottom-summary";
export const HIDE_INACTIVE_GROUPS_CLASS = "hide-inactive-groups";

export function getWhopExperienceId(url?: string): string | null {
  if (typeof window === "undefined" && !url) return null;

  const match = window.location.pathname.match(/^\/whop\/experience\/([^/]+)/);
  return (
    (match ? match[1] : null) ??
    safeGetLocalStorageItem(STORAGE_WHOP_EXPERIENCE_ID_KEY)
  );
}

export const WHOP_EXPERIENCE_ID = getWhopExperienceId();

export const TELEGRAM_OAUTH_STATE_PARAM = "state";
